// THE FOLDER'S FACE — an emoji, always, unless a person picked one of the
// geometric marks by hand.
//
// A folder is a thing someone is building or thinking about, and a thing has a
// picture. The geometric palette (lib/os/symbols GLYPHS) reads as notation; an
// emoji reads as the thing. So every un-overridden FOLDER wears an emoji chosen
// from its name — a keyword lookup first (the obvious associations), then a
// stable pick from a curated set (hashed off the name, so a folder always wears
// the same face until renamed). Pages, dimensions and tasks keep their glyphs.
//
// The keyword list is deliberately broad and lowercase; first hit wins, longer
// keys are tried before shorter ones so "recipe book" lands on 🍳 before 📖.

const KEYWORDS: [string, string][] = [
  // people & relationships
  ["people", "🫂"], ["person", "🧑"], ["team", "🧑‍🤝‍🧑"], ["family", "👨‍👩‍👧"], ["friend", "🤝"], ["client", "🤝"],
  ["customer", "🛍️"], ["community", "🌐"], ["founder", "🧭"], ["hiring", "🪪"], ["contact", "📇"],
  // work & building
  ["startup", "🚀"], ["launch", "🚀"], ["company", "🏢"], ["business", "💼"], ["strategy", "♟️"], ["roadmap", "🗺️"],
  ["plan", "🗓️"], ["product", "📦"], ["feature", "✨"], ["design", "🎨"], ["brand", "🏷️"], ["marketing", "📣"],
  ["growth", "📈"], ["sales", "💹"], ["pricing", "💸"], ["finance", "💰"], ["budget", "💰"], ["invoice", "🧾"],
  ["legal", "⚖️"], ["contract", "📝"], ["research", "🔬"], ["experiment", "🧪"], ["data", "📊"], ["analytics", "📊"],
  ["metric", "📈"], ["report", "📄"], ["notes", "🗒️"], ["note", "🗒️"], ["journal", "📓"], ["diary", "📓"],
  ["idea", "💡"], ["brainstorm", "🧠"], ["memory", "🧠"], ["learning", "📚"], ["study", "📚"], ["course", "🎓"],
  ["school", "🏫"], ["thesis", "🎓"], ["book", "📖"], ["reading", "📖"], ["writing", "✍️"], ["blog", "✍️"],
  ["newsletter", "📬"], ["email", "✉️"], ["podcast", "🎙️"], ["video", "🎬"], ["film", "🎬"], ["youtube", "▶️"],
  ["photo", "📷"], ["camera", "📷"], ["music", "🎵"], ["song", "🎵"], ["album", "💿"], ["band", "🎸"], ["art", "🖼️"],
  ["paint", "🎨"], ["draw", "✏️"], ["illustration", "🖌️"], ["font", "🔤"], ["type", "🔤"],
  // tech
  ["code", "💻"], ["software", "💻"], ["app", "📱"], ["mobile", "📱"], ["ios", "📱"], ["android", "🤖"],
  ["web", "🌐"], ["website", "🌐"], ["site", "🌐"], ["api", "🔌"], ["server", "🖥️"], ["backend", "⚙️"],
  ["infra", "🏗️"], ["database", "🗄️"], ["security", "🔐"], ["privacy", "🔒"], ["auth", "🔑"], ["bug", "🐛"],
  ["ai", "🤖"], ["agent", "🤖"], ["model", "🧠"], ["robot", "🤖"], ["hardware", "🔩"], ["device", "📟"],
  ["game", "🎮"], ["gaming", "🎮"], ["crypto", "🪙"], ["blockchain", "⛓️"], ["cloud", "☁️"], ["open source", "🌱"],
  // life
  ["home", "🏡"], ["house", "🏠"], ["apartment", "🏢"], ["move", "📦"], ["garden", "🌿"], ["plant", "🪴"],
  ["cook", "🍳"], ["recipe", "🍳"], ["food", "🍽️"], ["restaurant", "🍽️"], ["coffee", "☕"], ["tea", "🍵"],
  ["wine", "🍷"], ["baking", "🥐"], ["health", "🫀"], ["fitness", "🏃"], ["gym", "🏋️"], ["run", "🏃"],
  ["yoga", "🧘"], ["sleep", "😴"], ["mind", "🧠"], ["therapy", "🌤️"], ["medical", "🩺"], ["doctor", "🩺"],
  ["baby", "🍼"], ["kid", "🧒"], ["wedding", "💍"], ["birthday", "🎂"], ["party", "🎉"], ["gift", "🎁"],
  ["travel", "✈️"], ["trip", "🧳"], ["flight", "✈️"], ["hotel", "🏨"], ["city", "🏙️"], ["japan", "🗾"],
  ["mountain", "⛰️"], ["hike", "🥾"], ["beach", "🏖️"], ["ocean", "🌊"], ["sea", "🌊"], ["boat", "⛵"],
  ["car", "🚗"], ["bike", "🚲"], ["space", "🪐"], ["astro", "🔭"], ["weather", "🌦️"], ["climate", "🌍"],
  ["nature", "🌲"], ["animal", "🐾"], ["dog", "🐶"], ["cat", "🐱"], ["bird", "🐦"], ["fish", "🐟"],
  ["sport", "⚽"], ["football", "⚽"], ["basketball", "🏀"], ["tennis", "🎾"], ["golf", "⛳"], ["chess", "♟️"],
  ["fashion", "👗"], ["cloth", "👕"], ["beauty", "💄"], ["fragrance", "🌸"], ["perfume", "🌸"], ["jewel", "💎"],
  ["shop", "🛒"], ["store", "🏬"], ["ecommerce", "🛒"], ["furniture", "🛋️"], ["interior", "🛋️"],
  ["car", "🚗"], ["energy", "⚡"], ["solar", "☀️"], ["water", "💧"], ["fire", "🔥"], ["earth", "🌍"],
  ["history", "🏛️"], ["philosophy", "🏛️"], ["religion", "🕊️"], ["faith", "🕊️"], ["language", "🗣️"], ["spanish", "🗣️"],
  ["math", "➗"], ["physics", "⚛️"], ["chemistry", "⚗️"], ["biology", "🧬"], ["medicine", "💊"],
  ["event", "📅"], ["conference", "🎤"], ["talk", "🎤"], ["meeting", "🗓️"], ["calendar", "📅"],
  ["task", "☑️"], ["todo", "☑️"], ["goal", "🎯"], ["habit", "🔁"], ["archive", "🗃️"], ["inbox", "📥"],
  ["love", "❤️"], ["dating", "💘"], ["dream", "🌙"], ["vision", "🔭"], ["mission", "🧭"], ["values", "🧭"],
  ["personal", "🪞"], ["self", "🪞"], ["life", "🌱"], ["default", "📁"],
];
// longest keys first, so the more specific association wins
KEYWORDS.sort((a, b) => b[0].length - a[0].length);

// a curated fallback set — pleasant, distinct, never a face or a flag
const POOL = [
  "📁", "🗂️", "📦", "🧭", "🌱", "🪴", "🌿", "🍃", "🌸", "🌼", "🪷", "🌊", "🏔️", "🪐", "🌙", "⭐", "✨",
  "💡", "🔮", "🎯", "🧩", "🪄", "🧵", "🪡", "🎈", "🪁", "🪶", "🧊", "🫧", "🔭", "🧪", "⚙️", "🔧",
  "🧱", "🏗️", "🗝️", "📐", "🎨", "🖌️", "📷", "🎬", "🎵", "📚", "📖", "✏️", "🗒️", "💎", "🪙", "🧠",
];

function hashOf(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** The emoji a folder wears from its name (and type). Deterministic. */
export function emojiFor(name: string, type?: string | null): string {
  const hay = ` ${(name + " " + (type ?? "")).toLowerCase().replace(/[_\-./]+/g, " ")} `;
  for (const [k, e] of KEYWORDS) if (hay.includes(k)) return e;
  return POOL[hashOf(name || "folder") % POOL.length];
}
